import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Blog = () => {
  const blogPosts = [
    {
      title: '10 Tips for Maintaining Bone Health',
      excerpt: 'Learn essential practices to keep your bones strong and prevent osteoporosis as you age. Expert advice from our orthopaedic specialists.',
      image: 'https://images.unsplash.com/photo-1516549655169-df83a0774514',
      author: 'Dr. Ashok Bangarshettar',
      date: 'Jan 15, 2024',
      category: 'Orthopedics',
      readTime: '5 min read',
    },
    {
      title: 'Understanding Eye Care: Prevention and Treatment',
      excerpt: 'Comprehensive guide to maintaining healthy vision and understanding common eye conditions. From our ophthalmology department.',
      image: 'https://images.unsplash.com/photo-1663151064065-cb334788f77d',
      author: 'Dr. Varsha Bangarshettar',
      date: 'Jan 12, 2024',
      category: 'Ophthalmology',
      readTime: '6 min read',
    },
    {
      title: 'Heart Health: Warning Signs You Shouldn\'t Ignore',
      excerpt: 'Know the early warning signs of heart problems and when to seek immediate medical attention. Expert insights from cardiology.',
      image: 'https://images.unsplash.com/photo-1564732005956-20420ebdab60',
      author: 'Dr. Shivappa A',
      date: 'Jan 10, 2024',
      category: 'Cardiology',
      readTime: '7 min read',
    },
    {
      title: 'Emergency Care: What to Do Before Reaching Hospital',
      excerpt: 'Essential first aid tips and emergency response guidelines that everyone should know. From our emergency department.',
      image: 'https://images.unsplash.com/photo-1513224502586-d1e602410265',
      author: 'Emergency Team',
      date: 'Jan 8, 2024',
      category: 'Emergency Care',
      readTime: '4 min read',
    },
    {
      title: 'Post-Surgery Care: Road to Recovery',
      excerpt: 'A comprehensive guide to post-operative care and recovery. Tips for ensuring smooth healing after surgical procedures.',
      image: 'https://images.unsplash.com/photo-1640876777002-badf6aee5bcc',
      author: 'Dr. Sandhya N',
      date: 'Jan 5, 2024',
      category: 'Surgery',
      readTime: '8 min read',
    },
    {
      title: 'Maternal Health: Prenatal Care Essentials',
      excerpt: 'Everything expectant mothers need to know about prenatal care, nutrition, and preparing for childbirth.',
      image: 'https://images.pexels.com/photos/3957986/pexels-photo-3957986.jpeg',
      author: 'Dr. Shreedhar D',
      date: 'Jan 3, 2024',
      category: 'OBG',
      readTime: '6 min read',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7089625/pexels-photo-7089625.jpeg"
          alt="Blog"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 to-blue-900/70"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-white mb-4"
          >
            Health <span className="text-sky-300">Blog</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-white/90 max-w-2xl mx-auto"
          >
            Expert medical advice, health tips, and latest healthcare insights
          </motion.p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-2 text-gray-800">
              Featured <span className="text-sky-500">Article</span>
            </h2>
            <p className="text-gray-600">Latest insights from our medical experts</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border-none shadow-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-96 lg:h-auto">
                  <img
                    src={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-sky-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Featured
                  </div>
                </div>
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-block px-4 py-2 bg-sky-100 text-sky-600 rounded-full text-sm font-medium mb-4 w-fit">
                    {blogPosts[0].category}
                  </div>
                  <h3 className="text-3xl font-bold mb-4 text-gray-800">{blogPosts[0].title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-lg">{blogPosts[0].excerpt}</p>
                  <div className="flex items-center gap-6 text-sm text-gray-500 mb-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{blogPosts[0].author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{blogPosts[0].date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{blogPosts[0].readTime}</span>
                    </div>
                  </div>
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white w-fit">
                    Read Full Article
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
              Recent <span className="text-sky-500">Articles</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay informed with our latest health articles and medical news
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.slice(1).map((post, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-none shadow-xl hover:shadow-2xl transition-all overflow-hidden group h-full flex flex-col">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-sky-600 px-3 py-1 rounded-full text-xs font-semibold">
                      {post.category}
                    </div>
                  </div>
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold mb-3 text-gray-800 group-hover:text-sky-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed flex-1">{post.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4 flex-wrap">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{post.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full border-sky-500 text-sky-500 hover:bg-sky-500 hover:text-white"
                    >
                      Read More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-3xl p-12 text-white text-center shadow-2xl"
          >
            <h2 className="text-4xl font-bold mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
              Get the latest health tips and medical news delivered to your inbox
            </p>
            <div className="max-w-md mx-auto flex gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <Button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold">
                Subscribe
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;